import { useEffect } from "react";
import VestaDateInput from "./VestaDateInput";
import VestaSelect from "./VestaSelect";
import { descriptionCatalogOptions, findMunicipality, isItaly, municipalityCatalogOptions, municipalityCodeForValue } from "../../utils/customerCatalogs";
import { OverlayFieldError, OverlayFieldLabel, overlayFieldClass } from "./OverlayValidation";

const CONTACT_TYPE_OPTIONS = [
  { value: "Telefono", label: "Telefono" },
  { value: "Cellulare", label: "Cellulare" },
  { value: "Email", label: "Email" },
  { value: "PEC", label: "PEC" },
  { value: "Fax", label: "Fax" },
];

const DOCUMENT_TYPE_OPTIONS = [
  { value: "Carta identità", label: "Carta identità" },
  { value: "Passaporto", label: "Passaporto" },
  { value: "Patente", label: "Patente" },
  { value: "Permesso di soggiorno", label: "Permesso di soggiorno" },
];

const PREFIX_CONTACT_TYPES = new Set(["Telefono", "Cellulare", "Fax"]);
const DIGITS_ONLY_CONTACT_TYPES = new Set(["Telefono", "Cellulare"]);
const EMPTY_CONTACT = { contactType: "", countryPrefixId: "", contactValue: "", interlocutor: "", calls: false, notifications: false, emails: false, primary: false };
const EMPTY_DOCUMENT = { documentType: "", documentNumber: "", documentIssueCountry: "", documentIssuePlace: "", documentIssueDate: "", documentExpiryDate: "", documentIssuer: "", documentIsPrimary: false };

function cleanText(value) {
  return String(value || "").trim();
}

function alphabeticTitleCase(value) {
  return String(value || "")
    .replace(/[^\p{L}\s]/gu, "")
    .toLocaleLowerCase("it")
    .replace(/(^|\s)([\p{L}])/gu, (match, separator, letter) => `${separator}${letter.toLocaleUpperCase("it")}`);
}

function normalizeContactType(value) {
  const text = cleanText(value);
  const token = text.toLowerCase();
  if (["telefono", "tel", "fisso"].includes(token)) return "Telefono";
  if (["cellulare", "mobile", "cell", "whatsapp"].includes(token)) return "Cellulare";
  if (["email", "mail", "e-mail"].includes(token)) return "Email";
  if (token === "pec") return "PEC";
  if (token === "fax") return "Fax";
  return text;
}

export function contactPreferenceDefaults(contactType) {
  const normalizedType = normalizeContactType(contactType);
  const phoneEnabled = ["Telefono", "Cellulare", "Fax"].includes(normalizedType);
  const emailEnabled = ["Email", "PEC", "Fax"].includes(normalizedType);
  return { calls: phoneEnabled, notifications: phoneEnabled, emails: emailEnabled };
}

function normalizeContact(contact = {}) {
  return {
    contactType: normalizeContactType(contact.contactType || contact.tipoContatto || contact.type || contact.tipo),
    countryPrefixId: cleanText(contact.countryPrefixId || contact.prefixId || contact.prefissoId),
    countryPrefixDescription: cleanText(contact.countryPrefixDescription),
    countryPrefixValue: cleanText(contact.countryPrefixValue || contact.prefix),
    contactValue: String(contact.contactValue || contact.datoContatto || contact.value || contact.dato || ""),
    interlocutor: String(contact.interlocutor || contact.interlocutore || contact.referente || ""),
    calls: contact.calls === true || contact.chiamate === true,
    notifications: contact.notifications === true || contact.notifiche === true,
    emails: contact.emails === true || contact.mail === true,
    primary: contact.primary === true || contact.principale === true,
  };
}

function normalizeDocument(document = {}) {
  return {
    documentType: cleanText(document.documentType || document.documentoTipo || document.type || document.tipo),
    documentNumber: cleanText(document.documentNumber || document.documentoNumero || document.number || document.numero),
    documentIssueCountry: cleanText(document.documentIssueCountry || document.paeseRilascioDocumento || document.issueCountry),
    documentIssuePlace: String(document.documentIssuePlace || document.luogoRilascioDocumento || document.issuePlace || ""),
    documentIssueDate: cleanText(document.documentIssueDate || document.dataRilascioDocumento || document.issueDate),
    documentExpiryDate: cleanText(document.documentExpiryDate || document.dataScadenzaDocumento || document.expiryDate),
    documentIssuer: String(document.documentIssuer || document.enteRilascioDocumento || document.issuer || ""),
    documentIsPrimary: document.documentIsPrimary === true || document.documentoPrincipale === true || document.primary === true,
  };
}

function prefixDisplay(prefix) {
  const value = cleanText(prefix);
  if (!value) return "";
  return value.startsWith("+") ? value : `+${value}`;
}

function prefixLabel(description, prefix) {
  const countryLabel = cleanText(description);
  const prefixLabelValue = prefixDisplay(prefix);
  if (!countryLabel) return prefixLabelValue;
  return prefixLabelValue ? `${countryLabel} (${prefixLabelValue})` : countryLabel;
}

function prefixOptions(items = [], currentContact = {}) {
  const currentId = cleanText(currentContact.countryPrefixId);
  const currentPrefixValue = cleanText(currentContact.countryPrefixValue);
  const catalogItems = Array.isArray(items) ? items : [];
  const options = catalogItems
    .filter((item) => cleanText(item.prefix))
    .slice()
    .sort((a, b) => String(a.description || "").localeCompare(String(b.description || ""), "it"))
    .map((item) => ({ value: String(item._id), label: prefixLabel(item.description, item.prefix), displayLabel: prefixDisplay(item.prefix) }));

  if (currentId && !options.some((option) => option.value === currentId)) {
    const matchingCatalogItem = currentPrefixValue
      ? catalogItems.find((item) => cleanText(item.prefix) === currentPrefixValue)
      : null;
    const fallbackDescription = cleanText(currentContact.countryPrefixDescription || matchingCatalogItem?.description);
    const fallbackPrefix = cleanText(currentPrefixValue || matchingCatalogItem?.prefix);
    options.push({ value: currentId, label: prefixLabel(fallbackDescription, fallbackPrefix) || currentId, displayLabel: prefixDisplay(fallbackPrefix) || currentId });
  }
  return options;
}

function defaultCountryPrefixId(items = [], country = "") {
  const wanted = cleanText(country).toLocaleLowerCase("it");
  if (!wanted) return "";
  const exact = (items || []).find((item) => cleanText(item.prefix) && cleanText(item.description).toLocaleLowerCase("it") === wanted);
  if (exact?._id) return String(exact._id);
  const partial = (items || []).find((item) => cleanText(item.prefix) && cleanText(item.description).toLocaleLowerCase("it").includes(wanted));
  return partial?._id ? String(partial._id) : "";
}

export function CustomerContactsList({ contacts = [], prefixCatalog = [], contactTypeCatalog = [], country = "", validationErrors = {}, onChange }) {
  const normalizedRows = (Array.isArray(contacts) ? contacts : []).map(normalizeContact);
  const rows = normalizedRows.length || !validationErrors["contacts.0.contactType"] ? normalizedRows : [{ ...EMPTY_CONTACT }];
  const defaultPrefixId = defaultCountryPrefixId(prefixCatalog, country);
  const contactTypeOptions = contactTypeCatalog.length ? descriptionCatalogOptions(contactTypeCatalog) : CONTACT_TYPE_OPTIONS;

  useEffect(() => {
    if (!defaultPrefixId) return;
    const selectedPrefix = (prefixCatalog || []).find((item) => String(item?._id || "") === String(defaultPrefixId));
    let changed = false;
    const next = rows.map((contact) => {
      if (!PREFIX_CONTACT_TYPES.has(contact.contactType) || contact.countryPrefixId) return contact;
      changed = true;
      return {
        ...contact,
        countryPrefixId: defaultPrefixId,
        countryPrefixDescription: cleanText(selectedPrefix?.description),
        countryPrefixValue: cleanText(selectedPrefix?.prefix),
      };
    });
    if (changed) onChange?.(next);
  }, [contacts, defaultPrefixId]);

  function updateContact(index, field, value) {
    const next = rows.map((contact, rowIndex) => {
      if (rowIndex !== index) return contact;
      const updated = { ...contact, [field]: value };

      if (field === "countryPrefixId") {
        const selectedPrefix = (prefixCatalog || []).find((item) => String(item?._id || "") === String(value || ""));
        updated.countryPrefixId = cleanText(value);
        updated.countryPrefixDescription = cleanText(selectedPrefix?.description);
        updated.countryPrefixValue = cleanText(selectedPrefix?.prefix);
      }

      if (field === "contactType") {
        const previousType = normalizeContactType(contact.contactType);
        const nextType = normalizeContactType(value);
        const previousPreferences = contactPreferenceDefaults(previousType);
        const nextPreferences = contactPreferenceDefaults(nextType);
        updated.contactType = nextType;
        if (DIGITS_ONLY_CONTACT_TYPES.has(nextType)) updated.contactValue = String(updated.contactValue || "").replace(/\D/g, "");
        if (PREFIX_CONTACT_TYPES.has(nextType)) {
          updated.countryPrefixId = updated.countryPrefixId || defaultPrefixId;
          const selectedPrefix = (prefixCatalog || []).find((item) => String(item?._id || "") === String(updated.countryPrefixId || ""));
          updated.countryPrefixDescription = cleanText(selectedPrefix?.description || updated.countryPrefixDescription);
          updated.countryPrefixValue = cleanText(selectedPrefix?.prefix || updated.countryPrefixValue);
        } else {
          updated.countryPrefixId = "";
          updated.countryPrefixDescription = "";
          updated.countryPrefixValue = "";
        }
        updated.calls = nextPreferences.calls ? (previousPreferences.calls ? updated.calls === true : true) : false;
        updated.notifications = nextPreferences.notifications ? (previousPreferences.notifications ? updated.notifications === true : true) : false;
        updated.emails = nextPreferences.emails ? (previousPreferences.emails ? updated.emails === true : true) : false;
      }
      return updated;
    });
    onChange?.(next);
  }

  function addContact() {
    onChange?.([...rows, { ...EMPTY_CONTACT }]);
  }

  function removeContact(index) {
    onChange?.(rows.filter((_, rowIndex) => rowIndex !== index));
  }

  return (
    <div className="overlay_list_editor customer_registry_list_editor">
      <div className="overlay_list_toolbar">
        <button type="button" className="overlay_list_add_button" onClick={addContact} aria-label="Aggiungi contatto" title="Aggiungi contatto" tabIndex={-1}>
          <svg viewBox="0 0 24 24" aria-hidden="true" className="overlay_action_icon overlay_add_icon"><path d="M12 5v14M5 12h14" /></svg>
        </button>
        <p>Aggiungi i recapiti del cliente. Telefono, Cellulare e Fax possono usare il prefisso internazionale configurato nelle Nazioni.</p>
      </div>
      {rows.length === 0 ? (
        <div className="overlay_list_empty">Nessun contatto inserito.</div>
      ) : (
        <div className="overlay_list_rows customer_registry_list_rows">
          {rows.map((contact, index) => {
            const hasPrefix = PREFIX_CONTACT_TYPES.has(contact.contactType);
            const prefixRequired = DIGITS_ONLY_CONTACT_TYPES.has(contact.contactType);
            const communicationPreferences = contactPreferenceDefaults(contact.contactType);
            return (
              <div className="overlay_list_row customer_contact_list_row" key={`customer-contact-${index}`}>
                <label className={overlayFieldClass(validationErrors[`contacts.${index}.contactType`])} data-field-key={`contacts.${index}.contactType`}><OverlayFieldLabel required>Tipo Contatto</OverlayFieldLabel><VestaSelect value={contact.contactType || ""} options={contactTypeOptions} placeholder="Seleziona tipo" onValueChange={(value) => updateContact(index, "contactType", value)} aria-label="Tipo Contatto" /><OverlayFieldError message={validationErrors[`contacts.${index}.contactType`]} /></label>
                <label className={overlayFieldClass(validationErrors[`contacts.${index}.countryPrefixId`], hasPrefix ? "" : "is_disabled")} data-field-key={`contacts.${index}.countryPrefixId`}><OverlayFieldLabel required={prefixRequired}>Prefisso</OverlayFieldLabel><VestaSelect value={hasPrefix ? (contact.countryPrefixId || defaultPrefixId) : ""} options={prefixOptions(prefixCatalog, contact)} placeholder={hasPrefix ? "Seleziona prefisso" : ""} disabled={!hasPrefix} onValueChange={(value) => updateContact(index, "countryPrefixId", value)} aria-label="Prefisso" /><OverlayFieldError message={validationErrors[`contacts.${index}.countryPrefixId`]} /></label>
                <label className={overlayFieldClass(validationErrors[`contacts.${index}.contactValue`])} data-field-key={`contacts.${index}.contactValue`}><OverlayFieldLabel required>Contatto</OverlayFieldLabel><input inputMode={DIGITS_ONLY_CONTACT_TYPES.has(contact.contactType) ? "numeric" : undefined} value={contact.contactValue || ""} onChange={(event) => updateContact(index, "contactValue", DIGITS_ONLY_CONTACT_TYPES.has(contact.contactType) ? event.target.value.replace(/\D/g, "") : event.target.value)} onBlur={() => updateContact(index, "contactValue", cleanText(contact.contactValue))} /><OverlayFieldError message={validationErrors[`contacts.${index}.contactValue`]} /></label>
                <label className={overlayFieldClass(validationErrors[`contacts.${index}.interlocutor`])} data-field-key={`contacts.${index}.interlocutor`}><span>Interlocutore</span><input value={contact.interlocutor || ""} onChange={(event) => updateContact(index, "interlocutor", alphabeticTitleCase(event.target.value))} onBlur={() => updateContact(index, "interlocutor", cleanText(contact.interlocutor))} /><OverlayFieldError message={validationErrors[`contacts.${index}.interlocutor`]} /></label>
                <label className={`overlay_check_field customer_list_check_field${communicationPreferences.calls ? "" : " is_disabled"}`}><span>Chiamate</span><input type="checkbox" checked={contact.calls === true} disabled={!communicationPreferences.calls} onChange={(event) => updateContact(index, "calls", event.target.checked)} /></label>
                <label className={`overlay_check_field customer_list_check_field${communicationPreferences.notifications ? "" : " is_disabled"}`}><span>Notifiche</span><input type="checkbox" checked={contact.notifications === true} disabled={!communicationPreferences.notifications} onChange={(event) => updateContact(index, "notifications", event.target.checked)} /></label>
                <label className={`overlay_check_field customer_list_check_field${communicationPreferences.emails ? "" : " is_disabled"}`}><span>Mail</span><input type="checkbox" checked={contact.emails === true} disabled={!communicationPreferences.emails} onChange={(event) => updateContact(index, "emails", event.target.checked)} /></label>
                <label className={overlayFieldClass(validationErrors[`contacts.${index}.primary`], "overlay_check_field customer_list_check_field")} data-field-key={`contacts.${index}.primary`}><span>Principale</span><input type="checkbox" checked={contact.primary === true} onChange={(event) => updateContact(index, "primary", event.target.checked)} /><OverlayFieldError message={validationErrors[`contacts.${index}.primary`]} /></label>
                <button type="button" className="overlay_contact_remove_button overlay_list_remove_button customer_list_remove_button" onClick={() => removeContact(index)} aria-label="Rimuovi contatto" title="Rimuovi contatto" tabIndex={-1}>×</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CustomerDocumentsList({ documents = [], documentTypeCatalog = [], countryCatalog = [], municipalityCatalog = [], validationErrors = {}, onChange, onScanDocument }) {
  const normalizedRows = (Array.isArray(documents) ? documents : []).map(normalizeDocument);
  const rows = normalizedRows.length || !validationErrors["documents.0.documentType"] ? normalizedRows : [{ ...EMPTY_DOCUMENT }];
  const documentTypeOptions = documentTypeCatalog.length ? descriptionCatalogOptions(documentTypeCatalog) : DOCUMENT_TYPE_OPTIONS;

  function documentTypeOptionsForRow(index) {
    const currentType = cleanText(rows[index]?.documentType).toLocaleLowerCase("it");
    const usedTypes = new Set(
      rows
        .filter((_, rowIndex) => rowIndex !== index)
        .map((document) => cleanText(document.documentType).toLocaleLowerCase("it"))
        .filter(Boolean),
    );
    return documentTypeOptions.filter((option) => {
      const optionType = cleanText(option?.value).toLocaleLowerCase("it");
      return optionType === currentType || !usedTypes.has(optionType);
    });
  }

  function updateDocument(index, field, value) {
    onChange?.(rows.map((document, rowIndex) => rowIndex === index ? { ...document, [field]: value } : document));
  }

  function addDocument() {
    onChange?.([...rows, { ...EMPTY_DOCUMENT }]);
  }

  function removeDocument(index) {
    onChange?.(rows.filter((_, rowIndex) => rowIndex !== index));
  }

  return (
    <div className="overlay_list_editor customer_registry_list_editor">
      <div className="overlay_list_toolbar">
        <button type="button" className="overlay_list_add_button" onClick={addDocument} aria-label="Aggiungi documento" title="Aggiungi documento" tabIndex={-1}>
          <svg viewBox="0 0 24 24" aria-hidden="true" className="overlay_action_icon overlay_add_icon"><path d="M12 5v14M5 12h14" /></svg>
        </button>
        {typeof onScanDocument === "function" ? (
          <button type="button" className="overlay_list_add_button customer_document_scan_button" onClick={onScanDocument} aria-label="Scansiona documento da fotocamera" title="Scansiona documento da fotocamera" tabIndex={-1}>
            <svg viewBox="0 0 24 24" aria-hidden="true" className="overlay_action_icon customer_document_scan_icon"><path d="M8.5 7 10 5h4l1.5 2H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3.5Z"/><circle cx="12" cy="13" r="3.25"/></svg>
          </button>
        ) : null}
        <p>Gestisci tutti i documenti del cliente in elenco, compresi dati di rilascio, scadenza ed eventuale documento principale.</p>
      </div>
      {rows.length === 0 ? (
        <div className="overlay_list_empty">Nessun documento inserito.</div>
      ) : (
        <div className="overlay_list_rows customer_registry_list_rows">
          {rows.map((document, index) => (
            <div className="overlay_list_row customer_document_list_row" key={`customer-document-${index}`}>
              <label className={overlayFieldClass(validationErrors[`documents.${index}.documentType`])} data-field-key={`documents.${index}.documentType`}><OverlayFieldLabel required>Tipo Documento</OverlayFieldLabel><VestaSelect value={document.documentType || ""} options={documentTypeOptionsForRow(index)} placeholder="Seleziona tipo" onValueChange={(value) => updateDocument(index, "documentType", value)} aria-label="Tipo Documento" /><OverlayFieldError message={validationErrors[`documents.${index}.documentType`]} /></label>
              <label className={overlayFieldClass(validationErrors[`documents.${index}.documentNumber`])} data-field-key={`documents.${index}.documentNumber`}><OverlayFieldLabel required>Numero</OverlayFieldLabel><input value={document.documentNumber || ""} onChange={(event) => updateDocument(index, "documentNumber", event.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())} /><OverlayFieldError message={validationErrors[`documents.${index}.documentNumber`]} /></label>
              <label className="overlay_field"><span>Paese Rilascio</span><VestaSelect value={document.documentIssueCountry || ""} options={descriptionCatalogOptions(countryCatalog, document.documentIssueCountry)} placeholder="Seleziona paese" onValueChange={(value) => updateDocument(index, "documentIssueCountry", value)} aria-label="Paese Rilascio" /></label>
              <label className="overlay_field"><span>Luogo Rilascio</span>{isItaly(document.documentIssueCountry) ? <VestaSelect value={municipalityCodeForValue(municipalityCatalog, document.documentIssuePlace)} options={municipalityCatalogOptions(municipalityCatalog)} placeholder="Seleziona comune" onValueChange={(value) => updateDocument(index, "documentIssuePlace", findMunicipality(municipalityCatalog, municipalityCatalog.find((item) => String(item?.code || "") === String(value || ""))?.description || "")?.description || "")} aria-label="Luogo Rilascio" /> : <input value={document.documentIssuePlace || ""} onChange={(event) => updateDocument(index, "documentIssuePlace", event.target.value)} onBlur={() => updateDocument(index, "documentIssuePlace", cleanText(document.documentIssuePlace))} />}</label>
              <label className={overlayFieldClass(validationErrors[`documents.${index}.documentIssueDate`], "overlay_field_action")} data-field-key={`documents.${index}.documentIssueDate`}><span>Data Rilascio</span><VestaDateInput value={document.documentIssueDate || ""} onChange={(value) => updateDocument(index, "documentIssueDate", value)} /><OverlayFieldError message={validationErrors[`documents.${index}.documentIssueDate`]} /></label>
              <label className={overlayFieldClass(validationErrors[`documents.${index}.documentExpiryDate`], "overlay_field_action")} data-field-key={`documents.${index}.documentExpiryDate`}><span>Scadenza</span><VestaDateInput value={document.documentExpiryDate || ""} onChange={(value) => updateDocument(index, "documentExpiryDate", value)} /><OverlayFieldError message={validationErrors[`documents.${index}.documentExpiryDate`]} /></label>
              <label className={overlayFieldClass(validationErrors[`documents.${index}.documentIssuer`])} data-field-key={`documents.${index}.documentIssuer`}><span>Ente Rilascio</span><input value={document.documentIssuer || ""} onChange={(event) => updateDocument(index, "documentIssuer", event.target.value.replace(/[^\p{L}\p{N} ]/gu, ""))} onBlur={() => updateDocument(index, "documentIssuer", cleanText(document.documentIssuer))} /><OverlayFieldError message={validationErrors[`documents.${index}.documentIssuer`]} /></label>
              <label className={overlayFieldClass(validationErrors[`documents.${index}.documentIsPrimary`], "overlay_check_field customer_list_check_field")} data-field-key={`documents.${index}.documentIsPrimary`}><span>Principale</span><input type="checkbox" checked={document.documentIsPrimary === true} onChange={(event) => updateDocument(index, "documentIsPrimary", event.target.checked)} /><OverlayFieldError message={validationErrors[`documents.${index}.documentIsPrimary`]} /></label>
              <button type="button" className="overlay_contact_remove_button overlay_list_remove_button customer_list_remove_button" onClick={() => removeDocument(index)} aria-label="Rimuovi documento" title="Rimuovi documento" tabIndex={-1}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
